/* eslint-disable jsx-a11y/label-has-associated-control */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Button, Modal, Spinner } from '../../../shared/components';
import { SaveCustomFilter } from '../../../services/types/CustomFilter';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-bootstrap-typeahead/css/Typeahead.css';

interface Props {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  handleSubmit: (values: SaveCustomFilter, resetFormCallback: () => void) => void;
  handleCancel: () => void;
  size: string;
  currentOperation: { [key: string]: string };
}

const saveCustomFilterSchema = Yup.object().shape({
  name: Yup.string().required('required'),
});

const SaveCustomFiltersModal = (props: Props) => {
  const { visible, setVisible, handleSubmit, handleCancel, size, currentOperation } = props;
  const { t } = useTranslation();

  return (
    <Modal
      title={t('saveCustomFilterFormTitle')}
      visible={visible}
      setVisible={setVisible}
      size={size}
    >
      <Formik
        initialValues={{ name: '' }}
        validationSchema={saveCustomFilterSchema}
        validateOnMount
        onSubmit={(values, { resetForm }) => {
          const payload = { ...values } as SaveCustomFilter;
          handleSubmit(payload, resetForm);
        }}
        onReset={() => {
          // Formik's resetForm() already invokes this onReset handler
          // itself (see formik/dist/formik.cjs.development.js) - calling
          // resetForm() again from in here re-enters it and recurses until
          // the call stack overflows. handleReset (bound to the form's
          // native reset event) already calls resetForm() before this
          // runs, so nothing here needs to reset the form again.
          handleCancel();
        }}
      >
        {(formik) => (
          <form className="form" onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
            <div className="modal__body">
              <div className="finish-modal modal-dialog--custom-filter">
                <p>
                  <span>{t('saveCustomFilterFormFor')}</span>
                  {currentOperation && (
                    <strong>
                      {currentOperation.assignedText} / {currentOperation.queryGroupText}
                    </strong>
                  )}
                </p>
                <br />
                <div className="form-row">
                  <div className="col-sm-12">
                    <label className="col-form-label">{t('filterName')}</label>
                    <input
                      id="name"
                      placeholder="Ex. Filters 1"
                      name="name"
                      type="text"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.name}
                    />
                    {formik.touched.name && formik.errors.name && (
                      <span className="form__form-group-error">{formik.errors.name}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal__footer modal__foote--save-custom-filter">
              <Spinner loading={formik.isSubmitting} />
              <Button
                key="btnCancel"
                className="btn-tertiary"
                text={t('cancel')}
                type="reset"
                disabled={formik.isSubmitting}
              />
              <Button
                key="btnApplyAndSave"
                className="btn-primary"
                text={t('save')}
                type="submit"
                disabled={formik.isSubmitting || !formik.isValid}
              />
            </div>
            <br />
          </form>
        )}
      </Formik>
    </Modal>
  );
};

export default SaveCustomFiltersModal;
